import React, { useState, useEffect } from 'react';
import {
  Receipt,
  Search,
  Printer,
  IndianRupee,
  User,
  Clock,
  Edit3,
  Save,
  X,
  Info
} from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { bookingsAPI, stationsAPI } from '../../services/api';
import { computeBillTotals, sanitiseAmountInput } from '../../utils/billing';
import { NOTE_MAX } from '../../utils/bookingValidation';
import toast from 'react-hot-toast';

const Billing = () => {
  const { admin } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedStation, setSelectedStation] = useState('');
  const [stations, setStations] = useState([]);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Editable bill state
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editSubtotal, setEditSubtotal] = useState('0');
  const [editGstPct, setEditGstPct] = useState(0);
  const [editExtraCharges, setEditExtraCharges] = useState('0');
  const [editExtraNote, setEditExtraNote] = useState('');
  const [editDiscount, setEditDiscount] = useState('0');
  const [editDiscountNote, setEditDiscountNote] = useState('');

  useEffect(() => {
    if (admin?.role === 'super_admin') {
      fetchStations();
    } else if (admin?.assignedStation) {
      setSelectedStation(admin.assignedStation._id);
    }
  }, [admin]);

  useEffect(() => {
    if (selectedStation) {
      fetchBillingData();
    }
  }, [selectedStation]);

  const fetchStations = async () => {
    try {
      const response = await stationsAPI.getAll();
      setStations(response.data.stations);
      if (response.data.stations.length > 0) {
        setSelectedStation(response.data.stations[0]._id);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const response = await bookingsAPI.getRequests({ 
        station: selectedStation, 
        status: 'checked_out' 
      });
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBilling = (booking) => {
    setSelectedBilling(booking);
    setIsEditing(false);
    setEditSubtotal(String(booking.subtotal ?? booking.price ?? 0));
    setEditGstPct(booking.gstPercentage || 0);
    setEditExtraCharges(String(booking.extraCharges || 0));
    setEditExtraNote(booking.extraChargesNote || '');
    setEditDiscount(String(booking.discount || 0));
    setEditDiscountNote(booking.discountNote || '');
    setIsModalOpen(true);
  };

  // Totals come from the shared helper so the preview matches what the server will store.
  const editBill = computeBillTotals({
    subtotal: editSubtotal,
    extraCharges: editExtraCharges,
    discount: editDiscount,
    gstPercentage: editGstPct
  });

  const handleSaveBill = async () => {
    if (saving) return;

    if ((parseFloat(editDiscount) || 0) > editBill.grossAmount) {
      toast.error(`Discount cannot exceed the bill total of ₹${editBill.grossAmount}`);
      return;
    }

    setSaving(true);
    try {
      // `totalAmount` is deliberately not sent: the server recomputes it, so a stale or
      // tampered figure can never be written against a real invoice.
      const { data } = await bookingsAPI.updateBill(selectedBilling._id, {
        subtotal: editBill.subtotal,
        gstPercentage: editBill.gstPercentage,
        extraCharges: editBill.extraCharges,
        extraChargesNote: editExtraNote,
        discount: editBill.discount,
        discountNote: editDiscountNote
      });

      // Trust the server's version of the booking over a locally-assembled one.
      const updated = data.booking;
      setSelectedBilling(updated);
      setBookings(prev => prev.map(b => (b._id === updated._id ? { ...b, ...updated } : b)));

      setIsEditing(false);
      toast.success('Bill updated successfully');
    } catch (error) {
      console.error('Error updating bill:', error);
      toast.error(error.response?.data?.error || 'Failed to update bill');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const b = selectedBilling;
    if (!b) return;

    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow) {
      toast.error('Please allow popups to print invoices');
      return;
    }

    printWindow.document.write(buildInvoiceHTML({
      billing: b,
      subtotal: displaySubtotal,
      gstPct: displayGstPct,
      gstAmount: displayGstAmount,
      extraCharges: displayExtraCharges,
      extraNote: displayExtraNote,
      discount: displayDiscount,
      discountNote: displayDiscountNote,
      total: displayTotal
    }));
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    };
  };

  const filteredBookings = bookings.filter((booking) => {
    return booking.guestName.toLowerCase().includes(search.toLowerCase()) ||
           booking.mobile.includes(search) ||
           booking.prn?.toLowerCase().includes(search.toLowerCase()) ||
           booking.allocatedRoom?.roomNumber?.toLowerCase().includes(search.toLowerCase());
  });

  const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0);

  // Get display values (edited or original)
  const displaySubtotal = isEditing ? editBill.subtotal : (selectedBilling?.subtotal || selectedBilling?.price || 0);
  const displayGstPct = isEditing ? editBill.gstPercentage : (selectedBilling?.gstPercentage || 0);
  const displayGstAmount = isEditing ? editBill.gstAmount : (selectedBilling?.gstAmount || 0);
  const displayExtraCharges = isEditing ? editBill.extraCharges : (selectedBilling?.extraCharges || 0);
  const displayExtraNote = isEditing ? editExtraNote : (selectedBilling?.extraChargesNote || '');
  const displayDiscount = isEditing ? editBill.discount : (selectedBilling?.discount || 0);
  const displayDiscountNote = isEditing ? editDiscountNote : (selectedBilling?.discountNote || '');
  const displayTotal = isEditing ? editBill.totalAmount : (selectedBilling?.totalAmount || 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-midnight-900">
              Billing History
            </h1>
            <p className="text-midnight-600 mt-1">
              View completed bookings and billing records
            </p>
          </div>
          
          {admin?.role === 'super_admin' && (
            <select
              value={selectedStation}
              onChange={(e) => setSelectedStation(e.target.value)}
              className="input-field max-w-xs"
            >
              {stations.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Revenue Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold mt-1 flex items-center gap-1">
                  <IndianRupee className="w-7 h-7" />
                  {totalRevenue.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Receipt className="w-7 h-7" />
              </div>
            </div>
          </div>
          
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-midnight-500 text-sm">Total Checkouts</p>
                <p className="text-3xl font-bold text-midnight-900 mt-1">
                  {filteredBookings.length}
                </p>
              </div>
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <User className="w-7 h-7 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-midnight-500 text-sm">Avg. Duration</p>
                <p className="text-3xl font-bold text-midnight-900 mt-1">
                  {filteredBookings.length > 0 
                    ? Math.round(filteredBookings.reduce((sum, b) => sum + (b.duration || 0), 0) / filteredBookings.length)
                    : 0
                  } hrs
                </p>
              </div>
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                <Clock className="w-7 h-7 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
          <input
            type="text"
            placeholder="Search by name, mobile, PNR, or room..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-12"
          />
        </div>

        {/* Billing Table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-20">
              <Receipt className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-midnight-700">No billing records</h3>
              <p className="text-midnight-500 mt-2">Completed bookings will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-midnight-50 border-b border-midnight-100">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Guest</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Room</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Check-in</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Check-out</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Duration</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Amount</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-midnight-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-midnight-100">
                  {filteredBookings.map((booking) => (
                    <tr key={booking._id} className="hover:bg-midnight-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-semibold text-midnight-900">{booking.guestName}</div>
                          <div className="text-sm text-midnight-500">{booking.countryCode || '+91'} {booking.mobile}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-semibold text-midnight-900">
                            {booking.allocatedRoom?.roomNumber}
                          </div>
                          <div className="text-sm text-midnight-500">
                            {booking.allocatedCategory?.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-midnight-600 text-sm">
                        {booking.checkInAt 
                          ? new Date(booking.checkInAt).toLocaleString('en-IN', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            })
                          : '—'
                        }
                      </td>
                      <td className="px-6 py-4 text-midnight-600 text-sm">
                        {booking.checkOutAt 
                          ? new Date(booking.checkOutAt).toLocaleString('en-IN', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            })
                          : '—'
                        }
                      </td>
                      <td className="px-6 py-4 text-midnight-600">
                        {booking.duration} hrs
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-bold text-green-600 flex items-center gap-1">
                          <IndianRupee className="w-4 h-4" />
                          {booking.totalAmount?.toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleViewBilling(booking)}
                          className="p-2 text-railway-500 hover:text-railway-700 hover:bg-railway-50 rounded-lg transition-colors"
                          title="View Invoice"
                        >
                          <Receipt className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setIsEditing(false); }}
        title="Invoice"
        size="lg"
      >
        {selectedBilling && (
          <div className="space-y-4">
            {/* Invoice Header */}
            <div className="text-center border-b border-gray-200 pb-4">
              <img src="/Retiring Room Icon.svg" alt="Retiring Room" className="w-16 h-16 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-gray-900">Retiring Room</h2>
              <p className="text-gray-500 text-sm">{selectedBilling.station?.name}</p>
              <p className="text-xs text-gray-400 mt-1">TAX INVOICE</p>
            </div>
            
            {/* Guest & Room Details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h3 className="text-xs font-semibold text-gray-400 mb-1">GUEST</h3>
                <p className="font-semibold text-gray-900">{selectedBilling.guestName}</p>
                <p className="text-gray-600">PNR: {selectedBilling.pnr || selectedBilling.prn}</p>
                <p className="text-gray-600">Mobile: {selectedBilling.countryCode || '+91'} {selectedBilling.mobile}</p>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 mb-1">ROOM</h3>
                <p className="font-semibold text-gray-900">
                  Room {selectedBilling.allocatedRoom?.roomNumber}
                </p>
                <p className="text-gray-600">{selectedBilling.allocatedCategory?.name}</p>
                <p className="text-gray-600">
                  Floor: {selectedBilling.allocatedRoom?.floor || 'Ground'}
                </p>
              </div>
            </div>
            
            {/* Timeline */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-gray-400">Check-in</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(selectedBilling.checkInAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">Check-out</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(selectedBilling.checkOutAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">Duration</p>
                  <p className="font-semibold text-gray-900">{selectedBilling.duration} hours</p>
                </div>
              </div>
            </div>
            
            {/* Billing Table - Editable */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Description</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Room Charges */}
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-3">
                      <p className="text-gray-900">Room Charges</p>
                      <p className="text-xs text-gray-500">
                        {selectedBilling.allocatedCategory?.name} — {selectedBilling.durationSlot}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <input
                          type="text"
                          inputMode="decimal"
                          value={editSubtotal}
                          onChange={(e) => setEditSubtotal(sanitiseAmountInput(e.target.value))}
                          aria-label="Room charges"
                          className="w-28 text-right border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <span className="font-semibold text-gray-900">
                          {displaySubtotal.toLocaleString('en-IN')}
                        </span>
                      )}
                    </td>
                  </tr>

                  {/* Extra Charges — always ADDED */}
                  {(displayExtraCharges > 0 || isEditing) && (
                    <tr className="border-t border-gray-100 bg-orange-50/50">
                      <td className="px-4 py-3">
                        <p className="text-gray-900">Extra Charges <span className="text-xs text-orange-600">(added)</span></p>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editExtraNote}
                            onChange={(e) => setEditExtraNote(e.target.value.slice(0, NOTE_MAX))}
                            maxLength={NOTE_MAX}
                            placeholder="Reason (e.g. Laundry)"
                            className="mt-1 w-full border border-blue-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        ) : (
                          displayExtraNote && <p className="text-xs text-gray-500">{displayExtraNote}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={editExtraCharges}
                            onChange={(e) => setEditExtraCharges(sanitiseAmountInput(e.target.value))}
                            aria-label="Extra charges"
                            className="w-28 text-right border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="font-semibold text-orange-600">
                            {displayExtraCharges.toLocaleString('en-IN')}
                          </span>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* GST — charged on room charges + extra charges */}
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-3">
                      <p className="text-gray-900">GST</p>
                      {isEditing ? (
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-xs text-gray-500">@</span>
                          <input
                            type="number"
                            value={editGstPct}
                            onChange={(e) => setEditGstPct(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                            aria-label="GST percentage"
                            className="w-16 text-center border border-blue-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            min="0"
                            max="100"
                            step="0.5"
                          />
                          <span className="text-xs text-gray-500">% of ₹{editBill.taxableAmount.toLocaleString('en-IN')}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">@ {displayGstPct}%</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {displayGstAmount.toLocaleString('en-IN')}
                    </td>
                  </tr>

                  {/* Discount — always DEDUCTED. This is the field that replaces staff typing
                      negative extra charges to knock money off a bill. */}
                  {(displayDiscount > 0 || isEditing) && (
                    <tr className="border-t border-gray-100 bg-green-50/60">
                      <td className="px-4 py-3">
                        <p className="text-gray-900">
                          Discount / Concession <span className="text-xs text-green-700 font-medium">(deducted)</span>
                        </p>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editDiscountNote}
                            onChange={(e) => setEditDiscountNote(e.target.value.slice(0, NOTE_MAX))}
                            maxLength={NOTE_MAX}
                            placeholder="Reason (e.g. Senior citizen concession)"
                            className="mt-1 w-full border border-green-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                          />
                        ) : (
                          displayDiscountNote && <p className="text-xs text-gray-500">{displayDiscountNote}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-green-700 font-medium">−</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={editDiscount}
                              onChange={(e) => setEditDiscount(sanitiseAmountInput(e.target.value))}
                              aria-label="Discount amount"
                              className="w-24 text-right border border-green-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                            />
                          </div>
                        ) : (
                          <span className="font-semibold text-green-700">
                            − {displayDiscount.toLocaleString('en-IN')}
                          </span>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* Total */}
                  <tr className="border-t-2 border-gray-300 bg-green-50">
                    <td className="px-4 py-3 font-bold text-gray-900 text-base">Total</td>
                    <td className="px-4 py-3 text-right font-bold text-green-700 text-lg">
                      ₹{displayTotal.toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {isEditing && (
              <p className="flex items-start gap-1.5 text-xs text-midnight-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                <span>
                  Amounts must be positive. To reduce a bill use <strong>Discount / Concession</strong> —
                  never a negative extra charge. Every edit is recorded in the audit log with the
                  before and after figures.
                </span>
              </p>
            )}
            
            {/* Actions */}
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button 
                    onClick={() => setIsEditing(false)} 
                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveBill}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => setIsEditing(true)} 
                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Bill
                  </button>
                  <button 
                    onClick={handlePrint}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print Invoice
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

// Inline SVG logo for print (avoids image loading issues)
const LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 375 375"><defs><clipPath id="a"><path d="M105 123h158v153H105z"/></clipPath><clipPath id="b"><path d="M134 99h142v152H134z"/></clipPath><clipPath id="c"><path d="M98.5 141H219v135H98.5z"/></clipPath></defs><rect width="375" height="375" fill="#fff" rx="40"/><g clip-path="url(#a)"><path fill="#202049" d="M105.8 123.3c1.9 0 3.2 0 4.5 0 27.5 0 55 0 82.4.1 22.2.1 39.1 14.4 43 36.3 4 22.9-12 45.6-34.8 49.3-.8.1-1.5.3-2.6.6 1.7 2.1 3.2 4 4.7 5.9 7 8.8 13.9 17.6 20.9 26.3 6.1 7.5 13.3 13.5 23 15.5 3.8.8 7.8.8 11.8 1.1h3.5v17.5c-11.6.6-22.5-1.1-32.7-6.7-6.9-3.8-12.8-8.6-17.8-14.7-9.1-11.1-18-22.3-27.2-33.4-5-6-10.7-11.5-17.4-15.6-12.3-7.5-25.1-9.7-38.7-3.7-.2.1-.5.1-1.1.1 5.5-5.4 11.9-8.1 19-9.2 3.9-.6 7.9-.7 11.8-.7 11.9-.1 23.8.1 35.7 0 11.2-.1 20.7-7.3 23.7-17.6 3.2-11-1.8-22.7-10.1-28.9-4.5-3-9.5-4.4-15-4.4-20.6 0-41.2 0-61.8-.1-11-.1-19-4.9-23.8-14.9-.4-.8-.6-1.6-1-2.8z"/></g><g clip-path="url(#b)"><path fill="#0085ff" d="M134.8 116.4c5.5-4.5 11.1-7.6 17-10 49.4-20.9 105.1 4.2 120.4 55.9 9.3 31.5 2.3 60-19.4 84.8-4.1 4.7-5.2 4.8-10.7 1.2 1.3-1.4 2.6-2.8 3.9-4.2 17-17.7 24.7-38.8 22.8-63.2-2.9-37.3-30.6-67.6-67.6-73.9-18.4-3.1-36.1-.3-52.7 8.5-.9.5-2 .8-3 .8-3.4.1-6.8 0-10.8 0z"/></g><g clip-path="url(#c)"><path fill="#0085ff" d="M111.6 141.5c2 1.1 3.8 2.2 5.8 3.3-.2.4-.3.8-.5 1.2-25.1 41.8-7.6 95.3 35.8 115.3 18.2 8.4 37 9.9 56.4 4.9 2.7-.7 4.8-.8 6.6 1.6.7.9 1.7 1.5 2.6 2.3-24.7 10.5-65.5 8.6-94.6-21.1-29.8-30.3-32.1-76.5-12.1-107.5z"/></g><path fill="#202049" d="M138.9 191.3v-39.6h6.6v26.3h63.3v6.9h-63v5.1c-2.2.4-4.3.9-6.9 1.3zM164.2 175.1V165.2h30.4c3.8 0 7.5.8 10.2 3.5 1.8 1.7 3 3.9 4.4 5.9-.2.2-.4.4-.6.6h-44.4z"/><path fill="#202049" d="M154.5 162.7c3.7-.1 6.7 2.8 6.7 6.4.1 3.4-2.8 6.4-6.4 6.4-3.5 0-6.5-2.8-6.7-6.2-.1-3.7 2.7-6.6 6.4-6.6z"/></svg>`;

function buildInvoiceHTML({ billing, subtotal, gstPct, gstAmount, extraCharges, extraNote, discount, discountNote, total }) {
  const fmtDate = (d) => new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  const fmtNum = (n) => Number(n).toLocaleString('en-IN');
  const escape = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  let extraRow = '';
  if (extraCharges > 0) {
    extraRow = `<tr style="border-bottom:1px solid #ddd">
      <td style="padding:8px">Extra Charges${extraNote ? ` (${escape(extraNote)})` : ''}</td>
      <td style="padding:8px;text-align:right">${fmtNum(extraCharges)}</td>
    </tr>`;
  }

  // Shown as an explicit negative line so the guest can see exactly what came off.
  let discountRow = '';
  if (discount > 0) {
    discountRow = `<tr style="border-bottom:1px solid #ddd;color:#15803d">
      <td style="padding:8px">Discount / Concession${discountNote ? ` (${escape(discountNote)})` : ''}</td>
      <td style="padding:8px;text-align:right">- ${fmtNum(discount)}</td>
    </tr>`;
  }

  return `<!DOCTYPE html>
<html><head><title>Invoice - ${escape(billing.guestName)}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; padding: 30px 40px; }
  table { width:100%; border-collapse:collapse; }
</style>
</head><body>
  <div style="text-align:center;border-bottom:2px solid #333;padding-bottom:15px;margin-bottom:20px">
    ${LOGO_SVG}
    <h1 style="font-size:22px;margin:8px 0 2px">Retiring Room</h1>
    <p style="font-size:14px;color:#555">${escape(billing.station?.name || '')}</p>
    <p style="font-size:11px;color:#999;letter-spacing:2px;margin-top:4px">TAX INVOICE</p>
  </div>

  <div style="display:flex;justify-content:space-between;margin-bottom:18px;font-size:13px">
    <div>
      <p style="font-size:10px;font-weight:bold;color:#888;margin-bottom:4px">GUEST DETAILS</p>
      <p style="font-weight:bold;margin:2px 0">${escape(billing.guestName)}</p>
      <p style="margin:2px 0;color:#555">PNR: ${escape(billing.pnr || billing.prn || 'N/A')}</p>
      <p style="margin:2px 0;color:#555">Mobile: ${billing.mobile ? escape(`${billing.countryCode || '+91'} ${billing.mobile}`) : 'N/A'}</p>
    </div>
    <div style="text-align:right">
      <p style="font-size:10px;font-weight:bold;color:#888;margin-bottom:4px">ROOM DETAILS</p>
      <p style="font-weight:bold;margin:2px 0">Room ${escape(billing.allocatedRoom?.roomNumber || 'N/A')}</p>
      <p style="margin:2px 0;color:#555">${escape(billing.allocatedCategory?.name || '')}</p>
      <p style="margin:2px 0;color:#555">Floor: ${escape(billing.allocatedRoom?.floor || 'Ground')}</p>
    </div>
  </div>

  <div style="background:#f5f5f5;padding:10px 15px;border-radius:4px;margin-bottom:18px;font-size:12px;display:flex;justify-content:space-between">
    <div>
      <p style="color:#888;font-size:10px">Check-in</p>
      <p style="font-weight:bold">${fmtDate(billing.checkInAt)}</p>
    </div>
    <div style="text-align:center">
      <p style="color:#888;font-size:10px">Duration</p>
      <p style="font-weight:bold">${billing.duration} hours</p>
    </div>
    <div style="text-align:right">
      <p style="color:#888;font-size:10px">Check-out</p>
      <p style="font-weight:bold">${fmtDate(billing.checkOutAt)}</p>
    </div>
  </div>

  <table style="font-size:13px;margin-bottom:20px">
    <thead>
      <tr style="border-bottom:2px solid #333">
        <th style="text-align:left;padding:8px;font-weight:bold">Description</th>
        <th style="text-align:right;padding:8px;font-weight:bold">Amount (₹)</th>
      </tr>
    </thead>
    <tbody>
      <tr style="border-bottom:1px solid #ddd">
        <td style="padding:8px">Room Charges — ${escape(billing.allocatedCategory?.name || '')} (${escape(billing.durationSlot)})</td>
        <td style="padding:8px;text-align:right">${fmtNum(subtotal)}</td>
      </tr>
      ${extraRow}
      <tr style="border-bottom:1px solid #ddd">
        <td style="padding:8px">GST @ ${gstPct}%</td>
        <td style="padding:8px;text-align:right">${fmtNum(gstAmount)}</td>
      </tr>
      ${discountRow}
      <tr style="border-top:2px solid #333;font-weight:bold;font-size:15px">
        <td style="padding:10px 8px">TOTAL</td>
        <td style="padding:10px 8px;text-align:right">₹${fmtNum(total)}</td>
      </tr>
    </tbody>
  </table>

  <div style="text-align:center;border-top:1px solid #ddd;padding-top:15px;font-size:11px;color:#888">
    <p>Thank you for staying with us!</p>
    <p style="margin-top:4px">This is a computer-generated invoice.</p>
  </div>
</body></html>`;
}

export default Billing;
